import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { makeStyles, Switch, TextField, Checkbox, FormControlLabel } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import {
    clearOrderedAssays,
    addSelectedPunkt,
    setAssayTime,
    removeAssayFromOrderList,
} from '@cabinet-packages/redux';
import { setOrderVials } from '@cabinet-packages/redux/actions/actionsCreators';
import { createOrderWithInsurance } from '../api/orderApi';
import { useAssaysFilter } from '@cabinet-packages/hooks';
import { designTokens } from '../styles/designTokens';
import PageHeader from '../components/PageHeader';
import PatientSelector from '../components/PatientSelector';
import AddPatientModal from '../components/AddPatientModal';
import AssayCartItem from '../components/AssayCartItem';
import DatePicker from '../components/DatePicker';
import CartSummary from '../components/CartSummary';
import CollectionPointsDialog from '../components/CollectionPointsDialog';
import ConfirmationDialog from '../components/ConfirmationDialog';
import Button from '../components/Button';
import { useNotification } from '../context/NotificationContext';

const useStyles = makeStyles({
    root: {
        width: '100%',
        height: 'calc(100vh - 47px)',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: designTokens.colors.background.page,
        overflow: 'hidden',
    },
    content: {
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
        padding: `0 ${designTokens.spacing.lg}`,
        paddingBottom: designTokens.spacing.xl,
        '@media (max-width: 640px)': {
            padding: `0 ${designTokens.spacing.sm}`,
            paddingBottom: designTokens.spacing.xl,
        }
    },
    contentWrapper: {
        maxWidth: '1200px',
        margin: '0 auto',
    },
    section: {
        marginBottom: designTokens.spacing.xl,
    },
    sectionTitle: {
        ...designTokens.typography.h3,
        marginBottom: designTokens.spacing.md,
    },
    assaysList: {
        marginBottom: designTokens.spacing.lg,
    },
    emptyState: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: designTokens.spacing.xxxl,
        textAlign: 'center',
    },
    emptyText: {
        ...designTokens.typography.body,
        color: designTokens.colors.text.secondary,
        marginBottom: designTokens.spacing.lg,
    },
    scheduleSection: {
        maxWidth: '1200px',
        display: 'flex',
        flexDirection: 'column',
        margin: `${designTokens.spacing.lg} auto 0`,
        paddingLeft: designTokens.spacing.xs,
        marginBottom: designTokens.spacing.xl,
    },
    scheduleToggle: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
    },
    scheduleTitle: {
        ...designTokens.typography.h3,
        fontWeight: 400,
    },
    scheduleSubtitle: {
        ...designTokens.typography.body,
        color: designTokens.colors.text.secondary,
        marginTop: '4px',
    },
    noteSection: {
        marginBottom: designTokens.spacing.xl,
    },
    noteLabel: {
        ...designTokens.typography.body,
        display: 'block',
        marginBottom: designTokens.spacing.sm,
    },
    noteField: {
        width: '100%',
        boxSizing: 'border-box',
        display: 'block',
        padding: '18.5px 14px',
        borderRadius: designTokens.borderRadius.button,
        backgroundColor: designTokens.colors.background.page,
        border: `1px solid ${designTokens.colors.border}`,
        outline: 'none',
        resize: 'vertical',
        overflow: 'hidden',
        fontFamily: 'inherit',
        ...designTokens.typography.body,
        transition: 'border-color 200ms cubic-bezier(0, 0, 0.2, 1)',
        '&:hover': {
            borderColor: designTokens.colors.grey[300],
        },
        '&:focus': {
            borderColor: designTokens.colors.primary,
            borderWidth: '1px',
        },
    },
    checkbox: {
        color: designTokens.colors.primary,
        '&.Mui-checked': {
            color: designTokens.colors.primary,
        }
    },
    checkboxLabel: {
        ...designTokens.typography.body,
    },
    punktSection: {
        padding: designTokens.spacing.lg,
        backgroundColor: designTokens.colors.grey[100],
        borderRadius: designTokens.borderRadius.card,
        marginBottom: designTokens.spacing.xl,
    },
    punktInfo: {
        display: 'flex',
        alignItems: 'center',
        gap: designTokens.spacing.md,
        marginBottom: designTokens.spacing.md,
    },
    punktAddress: {
        ...designTokens.typography.body,
        flex: 1,
    },
    punktPlaceholder: {
        ...designTokens.typography.body,
        color: designTokens.colors.text.secondary,
    },
    insuranceSection: {
        display: 'flex',
        gap: designTokens.spacing.lg,
        marginBottom: designTokens.spacing.xl,
        '@media (max-width: 640px)': {
            flexDirection: 'column',
        }
    },
    insuranceFieldWrapper: {
        width: '300px',
        maxWidth: '100%',
    },
    paymentFieldWrapper: {
        width: '150px',
        maxWidth: '100%',
    },
    insuranceLabel: {
        ...designTokens.typography.body,
        display: 'block',
        marginBottom: designTokens.spacing.sm,
    },
    insuranceField: {
        width: '100%',
        '& .MuiOutlinedInput-root': {
            borderRadius: designTokens.borderRadius.input,
            backgroundColor: designTokens.colors.background.page,
            '& fieldset': {
                borderColor: designTokens.colors.border,
            },
            '&:hover fieldset': {
                borderColor: designTokens.colors.grey[300],
            },
            '&.Mui-focused fieldset': {
                borderColor: designTokens.colors.primary,
                borderWidth: '1px',
            }
        },
        '& .MuiInputBase-input': {
            ...designTokens.typography.body,
        },
    },
    paymentHelperText: {
        marginLeft: 0,
        marginRight: -10,
        whiteSpace: 'nowrap',
    },
    footer: {
        borderTop: `1px solid ${designTokens.colors.border}`,
        padding: designTokens.spacing.lg,
        backgroundColor: designTokens.colors.background.page,
    },
});

export default function OrderManager({ isComponentInShop }) {
    const history = useHistory();
    const classes = useStyles();
    const dispatch = useDispatch();

    const { catalog, complexResearch, actions, takeId } = useSelector(state => state.assaysCatalog);
    const { orderingList, totalPrice, amountOfItems, selectedPunkt } = useSelector(state => state.orderAssays);
    const { vials } = useSelector(state => state.calculator);
    const { patients } = useSelector(state => state.patients);

    const [selectedPatient, setSelectedPatient] = useState(null);
    const [patientsList, setPatientsList] = useState([]);
    const [addPatientModalOpen, setAddPatientModalOpen] = useState(false);
    const [collectionPointsDialogOpen, setCollectionPointsDialogOpen] = useState(false);
    const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
    const [scheduleEnabled, setScheduleEnabled] = useState(false);
    const [scheduledDate, setScheduledDate] = useState(null);
    const [note, setNote] = useState('');
    const [clinicPay, setClinicPay] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [insurancePolicy, setInsurancePolicy] = useState('');
    const [companyPayment, setCompanyPayment] = useState('');
    const [patientPayment, setPatientPayment] = useState('');
    const [companyPaymentError, setCompanyPaymentError] = useState(false);
    const [patientPaymentError, setPatientPaymentError] = useState(false);

    const { showSuccess, showError, showWarning } = useNotification();
    const previousAmountOfItems = useRef();
    const noteRef = useRef(null);

    const autoResizeNote = useCallback(() => {
        const el = noteRef.current;
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = el.scrollHeight + 'px';
    }, []);

    useEffect(() => {
        autoResizeNote();
    }, [note, autoResizeNote]);

    const createAssaysData = useCallback((ids) => ids.map(currId => (
        [...catalog, ...complexResearch, ...actions].find(
            currAssay => +currAssay.id === +currId.id && currAssay.type === currId.type && !currAssay.superGroupName
        )
    )).filter(Boolean), [catalog, complexResearch, actions]);

    const {
        backToInitialState,
        filteredState,
    } = useAssaysFilter(createAssaysData(orderingList), true);

    useEffect(() => {
        if (previousAmountOfItems.current !== amountOfItems) {
            backToInitialState();
            previousAmountOfItems.current = amountOfItems;
        }
    }, [amountOfItems, backToInitialState]);

    useEffect(() => {
        if (patients && patients.length > 0) {
            const formattedPatients = patients.map(p => ({
                id: p.id,
                fullname: p.fullname || `${p.lastname || ''} ${p.firstname || ''} ${p.fathername || ''}`.trim(),
                ...p
            }));
            setPatientsList(formattedPatients);
        }
    }, [patients]);

    useEffect(() => {
        if (amountOfItems === 0) {
            history.push('/catalog');
        }
    }, [amountOfItems, history]);

    useEffect(() => {
        setCompanyPayment((parseFloat(totalPrice) || 0).toFixed(2));
        setPatientPayment('');
        setCompanyPaymentError(false);
        setPatientPaymentError(false);
    }, [totalPrice]);

    const handlePatientChange = (patient) => {
        setSelectedPatient(patient);
    };

    const handleAddPatient = () => {
        setAddPatientModalOpen(true);
    };

    const handlePatientSelect = (patient, updatedPatientsList) => {
        setSelectedPatient(patient);
        if (updatedPatientsList) {
            setPatientsList(updatedPatientsList);
        }
    };

    const handleAssayRemove = (item) => {
        dispatch(removeAssayFromOrderList(item.id, item.price, () => {}, item.type));
    };

    const handleAssayInfo = (item) => {
        switch (item.type) {
            case "assay":
                history.push(`/assay-info/${item.id}`);
                break;
            case "action":
                history.push(`/actions-details/${item.id}`);
                break;
            case "complex":
                history.push(`/complex-info/${item.id}`);
                break;
            default:
                break;
        }
    };

    const handlePunktSelect = (punkt) => {
        dispatch(addSelectedPunkt(punkt));
    };

    const handleClearAll = () => {
        setClearConfirmOpen(true);
    };

    const handleClearConfirm = () => {
        dispatch(clearOrderedAssays());
        history.push('/catalog');
    };

    const computeOther = (value, total) => {
        if (value === '') return total.toFixed(2);
        const numValue = parseFloat(value) || 0;
        const remainder = Math.max(0, total - numValue);
        return remainder > 0 ? remainder.toFixed(2) : '';
    };

    const handleCompanyPaymentChange = (e) => {
        const value = e.target.value;
        if (value !== '' && !/^\d*\.?\d{0,2}$/.test(value)) return;

        setCompanyPayment(value);

        const numValue = parseFloat(value) || 0;
        const total = parseFloat(totalPrice) || 0;

        setCompanyPaymentError(numValue > total);
        setPatientPaymentError(false);
        setPatientPayment(computeOther(value, total));
    };

    const handlePatientPaymentChange = (e) => {
        const value = e.target.value;
        if (value !== '' && !/^\d*\.?\d{0,2}$/.test(value)) return;

        setPatientPayment(value);

        const numValue = parseFloat(value) || 0;
        const total = parseFloat(totalPrice) || 0;

        setPatientPaymentError(numValue > total);
        setCompanyPaymentError(false);
        setCompanyPayment(computeOther(value, total));
    };

    const handleCreateOrder = async () => {
        if (!selectedPatient) {
            showWarning('Оберіть пацієнта');
            return;
        }

        if (!selectedPunkt) {
            showWarning('Оберіть відділення');
            return;
        }

        setIsLoading(true);

        try {
            const services = createAssaysData(orderingList).map(item => ({
                idPacket: item.id,
                idConf: item.idConf,
                price: item.price,
                discount: item.discount || 0,
                amount: 1,
                debt: 0,
            }));

            let time = null;
            if (scheduleEnabled && scheduledDate) {
                time = scheduledDate.toISOString().slice(0, 10) + ' 00:00:00.0';
            } else {
                const futureDate = new Date();
                futureDate.setDate(futureDate.getDate() + 10);
                const weekDay = futureDate.getDay();
                if (weekDay === 6) {
                    futureDate.setDate(futureDate.getDate() + 2); // субота → понеділок
                } else if (weekDay === 0) {
                    futureDate.setDate(futureDate.getDate() + 1); // неділя → понеділок
                }
                time = futureDate.toISOString().slice(0, 10) + ' 00:00:00.0';
            }

            dispatch(setAssayTime({ date: time }));

            const paymentValue = companyPayment ? parseFloat(companyPayment) : 0;
            const insuranceInfo = {
                vaucher: insurancePolicy,
                payment: paymentValue,
                clinicPay: clinicPay,
            };

            const result = await createOrderWithInsurance(
                totalPrice,
                selectedPatient.id,
                0,
                services,
                selectedPunkt.idPunkt,
                time,
                note,
                insuranceInfo
            );

            if (result) {
                showSuccess('Замовлення успішно створено!');
                dispatch(clearOrderedAssays());
                dispatch(setOrderVials(null));
                history.push('/catalog');
            }
        } catch (error) {
            console.error('Error creating order:', error);
            showError('Помилка при створенні замовлення');
        } finally {
            setIsLoading(false);
        }
    };

    const handleTransitionToCatalog = () => {
        history.push('/catalog');
    };

    const isOrderDisabled = !selectedPatient || !selectedPunkt || amountOfItems === 0;

    if (amountOfItems === 0) {
        return (
            <div className={classes.root}>
                <PageHeader title="Кошик" />
                <div className={classes.content}>
                    <div className={classes.emptyState}>
                        <div className={classes.emptyText}>Кошик порожній</div>
                        <Button variant="primary" onClick={handleTransitionToCatalog}>
                            Замовити дослідження
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={classes.root}>
            <PageHeader
                title="Кошик"
                subtitle={`${amountOfItems} ${amountOfItems === 1 ? 'дослідження' : amountOfItems < 5 ? 'дослідження' : 'досліджень'}`}
            />

            <PatientSelector
                selectedPatient={selectedPatient}
                patients={patientsList}
                onChange={handlePatientChange}
                onAddPatient={handleAddPatient}
            />

            <div className={classes.content}>
                <div className={classes.contentWrapper}>
                    <div className={classes.section}>
                        <div className={classes.sectionTitle}>Обрані дослідження</div>
                        <div className={classes.assaysList}>
                            {filteredState.map((item, index) => (
                                <AssayCartItem
                                    key={`${item.id}-${item.type}-${index}`}
                                    item={item}
                                    onRemove={handleAssayRemove}
                                    onInfo={handleAssayInfo}
                                    takeId={takeId}
                                />
                            ))}
                        </div>
                    </div>

                    <div className={classes.insuranceSection}>
                        <div className={classes.insuranceFieldWrapper}>
                            <label className={classes.insuranceLabel}>Страховий поліс</label>
                            <TextField
                                className={classes.insuranceField}
                                variant="outlined"
                                value={insurancePolicy}
                                onChange={(e) => setInsurancePolicy(e.target.value)}
                                fullWidth
                            />
                        </div>
                        <div className={classes.paymentFieldWrapper}>
                            <label className={classes.insuranceLabel}>Оплата компанії</label>
                            <TextField
                                className={classes.insuranceField}
                                variant="outlined"
                                placeholder="0.00 грн"
                                value={companyPayment}
                                onChange={handleCompanyPaymentChange}
                                error={companyPaymentError}
                                helperText={companyPaymentError ? 'Перевищує повну вартість' : ''}
                                FormHelperTextProps={{ className: classes.paymentHelperText }}
                                fullWidth
                                inputProps={{
                                    inputMode: 'decimal',
                                }}
                            />
                        </div>
                        <div className={classes.paymentFieldWrapper}>
                            <label className={classes.insuranceLabel}>Оплата пацієнта</label>
                            <TextField
                                className={classes.insuranceField}
                                variant="outlined"
                                placeholder="0.00 грн"
                                value={patientPayment}
                                onChange={handlePatientPaymentChange}
                                error={patientPaymentError}
                                helperText={patientPaymentError ? 'Перевищує повну вартість' : ''}
                                FormHelperTextProps={{ className: classes.paymentHelperText }}
                                fullWidth
                                inputProps={{
                                    inputMode: 'decimal',
                                }}
                            />
                        </div>
                    </div>

                    <div className={classes.scheduleSection}>
                        <div className={classes.scheduleToggle}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={scheduleEnabled}
                                        onChange={(e) => setScheduleEnabled(e.target.checked)}
                                        color="primary"
                                    />
                                }
                                label={<span className={classes.scheduleTitle}>Запланувати дослідження</span>}
                            />
                        </div>
                        {scheduleEnabled && (
                            <>
                                <div className={classes.scheduleSubtitle}>
                                    Рекомендована дата здачі
                                </div>
                                <div style={{ marginTop: '8px', width: '170px' }}>
                                    <DatePicker
                                        value={scheduledDate}
                                        onChange={setScheduledDate}
                                        disablePast
                                        placeholder="Оберіть дату"
                                    />
                                </div>
                            </>
                        )}
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={clinicPay}
                                    onChange={(e) => setClinicPay(e.target.checked)}
                                    className={classes.checkbox}
                                />
                            }
                            label={<span className={classes.checkboxLabel}>Оплата по перерахунку партнерами</span>}
                            style={{ marginTop: designTokens.spacing.md }}
                        />
                    </div>

                    <div className={classes.noteSection}>
                        <label className={classes.noteLabel}>Нотатки:</label>
                        <textarea
                            ref={noteRef}
                            className={classes.noteField}
                            rows={1}
                            placeholder="Примітки до замовлення"
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            onInput={autoResizeNote}
                        />
                    </div>

                    <div className={classes.punktSection}>
                        <div className={classes.sectionTitle}>Відділення</div>
                        <div className={classes.punktInfo}>
                            {selectedPunkt ? (
                                <span className={classes.punktAddress}>
                                    {selectedPunkt.address}
                                </span>
                            ) : (
                                <span className={classes.punktPlaceholder}>
                                    Відділення не обрано
                                </span>
                            )}
                        </div>
                        <Button
                            variant="outlined"
                            onClick={() => setCollectionPointsDialogOpen(true)}
                            size="small"
                        >
                            {selectedPunkt ? 'Змінити відділення' : 'Обрати відділення'}
                        </Button>
                    </div>
                </div>
            </div>

            <div className={classes.footer}>
                <CartSummary
                    totalPrice={totalPrice}
                    companyPayment={companyPayment}
                    patientPayment={patientPayment}
                    onClearAll={handleClearAll}
                    onCreateOrder={handleCreateOrder}
                    disabled={isOrderDisabled}
                    isLoading={isLoading}
                />
            </div>

            <AddPatientModal
                open={addPatientModalOpen}
                onClose={() => setAddPatientModalOpen(false)}
                onPatientSelect={handlePatientSelect}
            />

            <CollectionPointsDialog
                open={collectionPointsDialogOpen}
                onClose={() => setCollectionPointsDialogOpen(false)}
                onSelect={handlePunktSelect}
                selectedPunkt={selectedPunkt}
            />

            <ConfirmationDialog
                open={clearConfirmOpen}
                onClose={() => setClearConfirmOpen(false)}
                onConfirm={handleClearConfirm}
                title="Очистити кошик?"
                text="Ви впевнені, що хочете видалити всі дослідження з кошика?"
                confirmText="Очистити"
                cancelText="Скасувати"
            />
        </div>
    );
}
